import classnames from 'classnames';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useState, Fragment } from 'react';

import { FormProcessTypes } from 'types';
import { Tooltip, Row, Col } from 'components-antd';

import {
  getActiveFormTabSelector,
  getFormsFiltersSelector,
} from 'store/selectors/requestFormProcess';

import { updateFormsFiltersAction } from 'store/actions/formProcess';
import { setFormsTabEffect } from 'store/effects/formProcess';
import { getUserRolesMapSelector } from 'store/selectors/user';
import { handleViewForm } from 'utils';
import { FormDetails } from './components/FormDetails';

import { ICON_VARIANT_TYPE, Icons } from './Icons';
import { FormsTableStatus } from 'app-constants';
import { FormProcessType } from 'types';
import { LocationService } from 'services';

import styles from './styles.module.scss';
import { NewFormModal } from './components/NewFormModal';
import { FormSelectionFooter } from './components/FormSelectionFooter';
import { MultiFormsUpdateModal } from './components/MultiFormsUpdateModal';

import _ from 'lodash';
import { useManageForms } from './hooks/useManageForms';
import { PageWrapper } from '../PageWrapper';
import { RevampedHeader } from '../components';
import classNames from 'classnames';
import { FormActionButtons } from './components/FormActionButtons';
import { PageSearchWrapper } from '../components/PageSearchWrapper';
import { FormsContent } from './components/FormsContent';

const locationService = new LocationService();

export type onHandleViewFormType = (url: string, type: FormProcessTypes) => void;

// Define a custom locale (e.g., 'en-short')
moment.defineLocale('en-short', {
  parentLocale: 'en',
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few sec',
    ss: '%d sec',
    m: '1 min',
    mm: '%d min',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1mo',
    MM: '%dmo',
    y: '1y',
    yy: '%dy',
  },
});

export const Forms = () => {
  const history = useHistory();
  const location: any = useLocation();
  locationService.setLocation(location);

  const dispatch = useDispatch();

  const { search, ...restFilters } = useSelector(getFormsFiltersSelector);

  const { isAgent, isClient } = useSelector(getUserRolesMapSelector);
  const [isSearchActive, setSearchActive] = useState(false);
  const { activeFormTab } = useSelector(getActiveFormTabSelector);

  if (!isAgent && !isClient) return <></>;

  const {
    loadingForms,
    onCloseFormDetailModal,
    setNewFormModal,
    selectedFormIds,
    selectedFormNames,
    setSelectedFormNames,
    setSelectedFormIds,
    showFormUpdateModal,
    setShowFormUpdateModal,
    fetchAllForms,
    setShowSelection,
    newFormModal,
    selectedFormProcess,
    selectedFormStatus,
    setSelectedFormStatus,
    setSelectedFormProcess,
    showSelection,
    isFilterApplied,
  } = useManageForms();

  const resetFormTab = () => {
    activeFormTab && dispatch(setFormsTabEffect(null));
  };
  // Toggling Visibility of New Form Modal
  const toggleNewFormModal = () => {
    setNewFormModal((prev) => !prev);
  };

  //Form View Handler
  const onHandleViewForm: onHandleViewFormType = (url, type) => {
    handleViewForm(history, url, type);
  };

  const handleSelectForm = (formProcessId: number, formProcessName, checked: boolean) => {
    let selectedForms = [...selectedFormIds];
    let selectedFormNames_ = [...selectedFormNames];

    if (checked) {
      selectedForms.push(formProcessId);
      selectedFormNames_.push(formProcessName);
    } else {
      const deselectedTaskIndex = selectedForms.indexOf(formProcessId);
      selectedForms.splice(deselectedTaskIndex, 1);
      selectedFormNames_.splice(deselectedTaskIndex, 1);
    }

    setSelectedFormNames(selectedFormNames_);
    setSelectedFormIds(selectedForms);
  };

  return (
    <>
      <PageWrapper
        isHideFooter={true}
        isContainer={false}
        pageWrapperContainerClassName={styles.mainPageContent}
      >
        <RevampedHeader
          className={classNames(styles.workshopPageHeader, {
            [styles.activeSearchWorkshopPageHeader]: isSearchActive,
          })}
          title={'Forms'}
          titleClassName={styles.headerTitle}
          actionButtons={
            <FormActionButtons
              showSelection={showSelection}
              onShowSelection={setShowSelection}
              onNewFormClick={toggleNewFormModal}
              onSearchClick={() => {
                resetFormTab();
                setSearchActive(true);
              }}
            />
          }
        />

        <PageSearchWrapper
          isSearchActive={isSearchActive}
          resetAction={() => updateFormsFiltersAction({ ...restFilters, search: undefined })}
          sendAction={({ search }) => updateFormsFiltersAction({ ...restFilters, search })}
          onCloseSearch={() => setSearchActive(false)}
          otherSearchParams={restFilters}
        />

        <FormsContent
          setSelectedFormProcess={
            isAgent
              ? (formId, formStatus) => {
                  setSelectedFormProcess(formId);
                  setSelectedFormStatus(formStatus);
                }
              : undefined
          }
          handleViewForm={onHandleViewForm}
          handleSelectForm={handleSelectForm}
          selectedFormIds={selectedFormIds}
          setSelectedFormIds={setSelectedFormIds}
          showSelectAll={true}
          showSelection={showSelection}
          refetchForms={() => fetchAllForms()}
          isPending={loadingForms}
          showListView={isSearchActive || isFilterApplied}
        />

        <NewFormModal isOpen={newFormModal} onClose={toggleNewFormModal} />

        <MultiFormsUpdateModal
          showFormUpdateModal={showFormUpdateModal}
          setShowFormUpdateModal={setShowFormUpdateModal}
          selectedFormIds={selectedFormIds}
          selectedFormNames={selectedFormNames}
          refetchForms={() => {
            resetFormTab();
            fetchAllForms();

            setSelectedFormIds([]);
            setShowSelection(false);
          }}
        />

        {selectedFormProcess && isAgent && (
          <FormDetails
            selectedFormProcess={selectedFormProcess}
            onClose={onCloseFormDetailModal}
            onHandleViewForm={onHandleViewForm}
            selectedFormStatus={selectedFormStatus}
            refetchForms={() => {
              resetFormTab();
              fetchAllForms();
            }}
            onDeleteFormSuccess={() => {
              onCloseFormDetailModal();
            }}
          />
        )}

        {showSelection && (
          <FormSelectionFooter
            selectedForms={selectedFormIds?.length}
            handleContinueFormUpdates={() => setShowFormUpdateModal(true)}
          />
        )}
      </PageWrapper>
    </>
  );
};

export const getTooltipInfo = (form, styles) => {
  if (form.isEdit) {
    return (
      <div className={styles.container}>
        <p className={styles.title}>Waiting on:</p>
        {form?.editors?.map((e) => (
          <Fragment key={`${form.url}-${e.editorId}`}>
            <div className={styles.editorRow}>
              <p className={styles.userName}>{e.isYou ? 'You' : e.name}</p>
            </div>
            <div className={styles.editorRow}>
              <p className={styles.formStatus}>
                {e.status}: {moment(e.date).fromNow()}
              </p>
            </div>
          </Fragment>
        ))}
      </div>
    );
  } else if (form.isSign || form.isFinalizing) {
    return (
      <div className={classnames(styles.container, styles.toolTipContainer)}>
        <p className={styles.title}>
          {form.isFinalizing ? 'Pending Digital Signature' : 'Waiting on:'}
        </p>
        {form?.signatories?.map((e) => {
          const hideDate = form.isSign && e.status.toLowerCase().includes('waiting on');
          return (
            <Row key={`${form.url}-${e.signatoryId}`} className={styles.row}>
              {form?.IsSequential ? (
                <Col className={styles.sequenceBoxColumn}>
                  <span
                    className={classNames(styles.sequenceBox, {
                      [styles.pendingSequence]: e.status === 'Requested',
                    })}
                  >
                    {e.order}
                  </span>
                </Col>
              ) : (
                <></>
              )}
              <Col className={styles.column}>
                <p className={styles.userName}>{e.isYou ? 'You' : e.name}</p>
                <p className={styles.formStatus}>
                  {e.status}
                  {!hideDate ? `: ${moment(e.date).locale('en-short').fromNow()}` : ''}
                </p>
              </Col>
              <Col span={2}>
                {e.status === 'Signed' ? (
                  <Icons variant={ICON_VARIANT_TYPE.SIGNED} />
                ) : (
                  <Icons variant={ICON_VARIANT_TYPE.PENDING} />
                )}
              </Col>
            </Row>
          );
        })}
      </div>
    );
  } else {
    return <></>;
  }
};

export const renderTooltip = (form: FormProcessType, children: any) => {
  const isCompleted = form.status === FormsTableStatus.Complete ? true : false;

  return (
    <Tooltip
      key={`${form.url}-tooltip`}
      trigger={'hover'}
      title={!isCompleted && !!(form?.editors || form?.signatories) && getTooltipInfo(form, styles)}
      overlayClassName={styles.toolTipContainer}
      color={'white'}
    >
      {children}
    </Tooltip>
  );
};
